import React from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import DateUtil from '../../../../utils/DateUtil';
import RemotingService from '../../../../services/remoting-service/RemotingService';
import DateInput from '../../../../components/date-input/DateInput';
import './ExtendValidityDialog.css';

/**
 * Dialog component for extending package validity
 * Allows users to select a new validity date and shows calculation details
 */
const ExtendValidityDialog = ({packageItem, handlePackageUpdate, close}) => {
    const [newValidityDate, setNewValidityDate] = React.useState(null);
    const [daysToAdd, setDaysToAdd] = React.useState(0);
    const [totalDays, setTotalDays] = React.useState(0);

    // Calculate days when date changes
    React.useEffect(() => {
        if (newValidityDate && packageItem.validUntil) {
            const currentValidUntil = new Date(packageItem.validUntil);
            const newDate = newValidityDate.toDate ? newValidityDate.toDate() : new Date(newValidityDate);

            if (newDate > currentValidUntil) {
                const timeDiff = newDate.getTime() - currentValidUntil.getTime();
                const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
                setDaysToAdd(daysDiff);

                // Calculate total days from today
                const today = new Date();
                const totalTimeDiff = newDate.getTime() - today.getTime();
                const totalDaysDiff = Math.ceil(totalTimeDiff / (1000 * 3600 * 24));
                setTotalDays(totalDaysDiff);
            } else {
                setDaysToAdd(0);
                setTotalDays(0);
            }
        }
    }, [newValidityDate]);

    const handleConfirm = () => {
        if (newValidityDate) {
            // Convert moment to Date if needed, then set time to end of day
            const dateValue = newValidityDate.toDate ? newValidityDate.toDate() : new Date(newValidityDate);
            const endOfDayDate = DateUtil.endOfDay(dateValue);

            RemotingService.postRemoteCall(
                `api/patients/${packageItem.patientId}/packages/${packageItem.id}/extend-validity`,
                {validUntil: DateUtil.formatDateTime(endOfDayDate)},
                (response) => {
                    handlePackageUpdate();
                    close();
                }
            );
        }
    };

    return (
        <div className="extend-validity-dialog">
            {/* Header */}
            <div className="dialog-header">
                <div className="header-title">
                    <FontAwesomeIcon
                        icon={["fas", "calendar-plus"]}
                        className="header-icon"
                    />
                    <h3 className="header-text">
                        Extend Package Validity
                    </h3>
                </div>
                <div className="header-info">
                    <FontAwesomeIcon
                        icon={["fas", "clock"]}
                        className="info-icon"
                    />
                    Current validity: <strong>
                    {DateUtil.formatDateMMM(packageItem.validUntil)}
                </strong>
                </div>
            </div>

            {/* Content */}
            <div className="dialog-content">
                <div className="input-section">
                    <label className="input-label">
                        <FontAwesomeIcon
                            icon={["fas", "calendar-day"]}
                            className="label-icon"
                        />
                        Select New Validity Date
                    </label>
                    <DateInput
                        value={newValidityDate}
                        onChange={setNewValidityDate}
                        minDate={DateUtil.addDays(new Date(packageItem.validUntil), 1)}
                        clearable={true}
                        placeholder="Choose extension date..."
                        className="date-input"
                        InputProps={{
                            className: "date-input-field"
                        }}
                    />
                </div>

                {newValidityDate && daysToAdd > 0 && (
                    <div className="calculation-card">
                        <div className="calc-row extension-row">
                            <FontAwesomeIcon
                                icon={["fas", "plus-circle"]}
                                className="calc-icon extension-icon"
                            />
                            <div className="calc-text">
                                <div className="calc-title">
                                    Extension: {daysToAdd} day{daysToAdd !== 1 ? 's' : ''}
                                </div>
                                <div className="calc-subtitle">
                                    Adding {daysToAdd} day{daysToAdd !== 1 ? 's' : ''} to current validity
                                </div>
                            </div>
                        </div>
                        <div className="calc-row total-row">
                            <FontAwesomeIcon
                                icon={["fas", "calendar-check"]}
                                className="calc-icon total-icon"
                            />
                            <div className="calc-text">
                                <div className="calc-title">
                                    New Total: {totalDays} day{totalDays !== 1 ? 's' : ''} from today
                                </div>
                                <div className="calc-subtitle">
                                    Valid
                                    until {DateUtil.formatDateDo(newValidityDate.toDate ? newValidityDate.toDate() : newValidityDate)}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {!newValidityDate && (
                    <div className="info-box">
                        <FontAwesomeIcon
                            icon={["fas", "info-circle"]}
                            className="info-box-icon"
                        />
                        <div className="info-text">
                            Please select a new validity date to see extension details
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="dialog-footer">
                <button
                    type="button"
                    className="btn btn-outline-secondary cancel-btn"
                    onClick={close}
                >
                    <FontAwesomeIcon
                        icon={["fas", "times"]}
                        className="btn-icon"
                    />
                    Cancel
                </button>
                <button
                    type="button"
                    className="btn btn-success confirm-btn"
                    onClick={handleConfirm}
                    disabled={!newValidityDate || daysToAdd <= 0}
                >
                    <FontAwesomeIcon
                        icon={["fas", "check"]}
                        className="btn-icon"
                    />
                    Extend Validity
                </button>
            </div>
        </div>
    );
};

export default ExtendValidityDialog; 